import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import LayoutNavSectionList from 'components/layout_and_quote_builder/LayoutNavSectionList.jsx';
import LayoutContext from 'components/contexts/LayoutContext.jsx';
import QuoteBuilderManagePanel from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderManagePanel.jsx';
import QuoteBuilderForm from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderForm.jsx';
import PresentedDielineOptions from 'components/layout_and_quote_builder/quote_builder/PresentedDielineOptions.jsx';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import frankStyles from 'components/frank/_style.module.scss';
import MyEventListener from 'components/common/MyEventListener.jsx';
import { CacheProvider } from 'components/layout_and_quote_builder/quote_builder/CacheContext.jsx';
import ErrorBoundary from 'components/common/ErrorBoundary.jsx';
import { readSelectedManufacturingLineList } from 'components/frank/utils/manufacturingLineListUrlState.js';
import { DebugPopupProvider } from 'components/layout_and_quote_builder/DebugPopup.jsx';
import FrankSetupWizard from 'components/frank/FrankSetupWizard.jsx';

// Frank's tabbed front end for the Manufacturing Cost Calculator, served at /mcc. It reuses the same content
// components the legacy stacked page (ShowLayoutAndQuoteBuilderPage) renders -- they already save to the
// user_quote, handle read-only, currency and status -- but lays them out as tabs instead of one long page:
//   Manage (quantity / windows / currency + status) | one tab per nav-builder section (e.g. Carton) |
//   Customer (name / email / phone / notes) | Dieline (material + dimensions).
// The section tabs render LayoutNavSectionList with a single section, so the existing Frank-mode gating
// (fetch Frank's section-material-sheets endpoint, hide the manufacturing lines + solutions) carries over.
const FrankQuoteBuilderPage = ({
  config, navSections, dimensionGroups, layoutOptions, formulaOptions, optionsUserEntered, unit, locale,
  theme, btn_action, dimension_texts_on_layouts_enabled, user_quote_id, dieline_svg, header
}) => {
  const LayoutContextValues = {
    dimensionGroups, layoutOptions, formulaOptions, optionsUserEntered, unit, locale, theme, btn_action,
    dimension_texts_on_layouts_enabled,
  };

  // The tab bar is TWO levels. The TOP level is one tab per nav-builder part section (e.g. Carton) plus the
  // global Manage, Customer and Dieline -- a single row that does not wrap. The 'general' section is the nav
  // builder's catch-all, not a manufacturing part_type, so it gets no tab. The SECOND level, shown only for
  // the active section, is that section's three stages (Manufacturing Line -> Solution -> Pricing): the three
  // views of its ManufacturingLineLists (the listing, the solution cards, the priced view). The user picks a
  // manufacturing line, then a solution, then sees the price -- a sequence, so the stages are numbered.
  const priceableSections = navSections
    .map((section, index) => ({ section, index }))
    .filter(({ section }) => section.part_type !== 'general');

  // The three stage tabs a section is split into, in order. `stage` is the ManufacturingLineLists stageView.
  const sectionStageTabs = [
    { suffix: 'line', stage: 'line', labelKey: 'quote_builder.frank_tab_manufacturing_line' },
    { suffix: 'solution', stage: 'solution', labelKey: 'quote_builder.frank_tab_solution' },
    { suffix: 'pricing', stage: 'pricing', labelKey: 'quote_builder.frank_tab_pricing' },
  ];
  const sectionTabs = priceableSections.flatMap(({ section, index }) => sectionStageTabs.map((stageTab) => ({
    key: `section_${index}_${stageTab.suffix}`,
    label: `${section.label || section.part_type} - ${t(stageTab.labelKey)}`,
    sectionIndex: index,
    stage: stageTab.stage,
  })));

  // The top-level tabs: ONE per priceable section (labelled by the section only -- the stage no longer rides
  // in the label, so "Carton for lamination" appears once, not three times) plus the global Manage, Customer
  // and Dieline. This keeps the bar to a single row.
  const topTabs = [
    { key: 'manage', label: t('quote_builder.manage') },
    ...priceableSections.map(({ section, index }) => ({
      key: `section_${index}`,
      label: section.label || section.part_type,
      sectionIndex: index,
    })),
    { key: 'customer', label: t('quote_builder.customer') },
    { key: 'dieline', label: t('quote_builder.dieline') },
  ];

  // Seed from the quote's own quantity (falling back to 1000, the create default), NOT a hardcoded 1000: the
  // server scores/solves against user_quote.count, and count is part of the ManufacturingLineResponseCache key,
  // so the page's count MUST match the quote's on load -- otherwise a reopened quote whose quantity is not 1000
  // would file its results under the wrong (count=1000) key. Mirrors FrankSetupWizard's seed.
  const [count, setCount] = useState((window.user_quote && window.user_quote.count) || 1000);
  // Open on the section a refresh / shared link picked a plan in (its manufacturing_line_<index> is in the
  // URL), so the restored selection reopens on its own section. Otherwise the first part section (e.g. Carton)
  // -- the manufacturing costs it lists are what the page is FOR, so it is what a user wants to see first. A
  // quote with no part section (only the nav builder's 'general' catch-all) has no section tab, and falls back
  // to Manage.
  const restoredSection = priceableSections.find(
    ({ index }) => readSelectedManufacturingLineList(index) !== null
  );
  const initialSectionIndex = restoredSection ? restoredSection.index : priceableSections[0]?.index;
  const [activeTab, setActiveTab] = useState(
    initialSectionIndex != null ? `section_${initialSectionIndex}_line` : 'manage'
  );

  // activeTab stays the single source of truth, a flat `section_<index>_<stage>` key (so the URL-restore,
  // initial-tab and wizard logic all keep working unchanged); the two-level bar is DERIVED from it. A
  // section's top tab is "active" when activeTab is any of that section's three stages.
  const activeSectionMatch = activeTab.match(/^section_(\d+)_(line|solution|pricing)$/);
  const activeSectionIndex = activeSectionMatch ? Number(activeSectionMatch[1]) : null;
  const activeStage = activeSectionMatch ? activeSectionMatch[2] : null;

  // Remember which stage each section was last left on, so returning to a section reopens where the user was
  // rather than jumping back to Manufacturing Line. Defaults to the first stage ('line') for an unvisited one.
  const lastStageBySection = useRef({});
  const openSection = (index) => {
    setActiveTab(`section_${index}_${lastStageBySection.current[index] || 'line'}`);
  };
  const openStage = (index, stage) => {
    lastStageBySection.current[index] = stage;
    setActiveTab(`section_${index}_${stage}`);
  };

  // The stage sub-menu hangs UNDER its parent section tab, like a drop-down sub-menu, rather than floating
  // centred. Since the active section tab's horizontal position depends on the labels before it, we measure
  // it: activeTabRef is put on the active section's top tab, tabRowRef on the row both levels start from, and
  // the sub-menu is indented by the gap between them. Re-measured whenever the active tab or the width changes.
  const tabRowRef = useRef(null);
  const activeTabRef = useRef(null);
  const [subMenuOffset, setSubMenuOffset] = useState(0);

  // The guided setup wizard auto-opens on a quote that has not been set up yet -- nothing priced -- so a fresh
  // quote is walked through quantity -> pick a line -> pick a solution per section. A quote that already has a
  // priced section opens straight to the page; the "Guided setup" button reopens the wizard any time. While it
  // is open the active tab's section body is not rendered, so the wizard's own ManufacturingLineLists is the
  // only one on screen (two would fight over the manufacturing_line_<index> URL state); closing it re-renders
  // the section, which reads the now-priced state the wizard persisted.
  const anySectionPriced = (window.user_quote_nav_sections || [])
    .some((section) => section.selected_lists && section.selected_lists.length > 0);
  const [wizardOpen, setWizardOpen] = useState(sectionTabs.length > 0 && !anySectionPriced);

  useEffect(() => { window.user_quote_id = user_quote_id; }, []);

  MyEventListener([
    { key: 'quote_builder.count_changed', callback: (msg) => { setCount(() => msg.detail); } }
  ]);

  const readOnly = window.user_quote_finalised;

  const isSectionTab = activeSectionIndex !== null;

  useLayoutEffect(() => {
    if (!isSectionTab || !activeTabRef.current || !tabRowRef.current) {
      setSubMenuOffset(0);
      return undefined;
    }
    const measure = () => {
      const tabLeft = activeTabRef.current.getBoundingClientRect().left;
      const rowLeft = tabRowRef.current.getBoundingClientRect().left;
      setSubMenuOffset(Math.max(0, tabLeft - rowLeft));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => { window.removeEventListener('resize', measure); };
  }, [activeTab, isSectionTab]);

  const renderActiveTab = () => {
    if (activeTab === 'manage') {
      return (
        <>
          <QuoteBuilderForm config={config.general} readOnly={readOnly} />
          <QuoteBuilderManagePanel readOnly={readOnly} />
        </>
      );
    }
    if (activeTab === 'customer') {
      return <QuoteBuilderForm config={config.customer} readOnly={readOnly} />;
    }
    if (activeTab === 'dieline') {
      // Show the dieline SVG (rendered server-side with its dimension texts) above the material +
      // dimension table, so the user can see the dieline being priced. dieline_svg is inline SVG markup.
      return (
        <>
          {dieline_svg && (
            <div
              style={{ maxWidth: '930px', margin: '0 auto 15px' }}
              dangerouslySetInnerHTML={{ __html: dieline_svg }}
            />
          )}
          <div style={{ maxWidth: '620px', margin: '0 auto' }}>
            <PresentedDielineOptions />
          </div>
        </>
      );
    }

    const sectionTab = sectionTabs.find((tab) => tab.key === activeTab);
    if (sectionTab) {
      // The stage AFTER the one on screen, in the section's fixed order (Manufacturing Line -> Solution ->
      // Pricing). The card's "Next" button walks the user there; the Pricing tab has no next, so it is null and
      // no Next button is offered on the priced view.
      const stageOrder = sectionStageTabs.map((stageTab) => stageTab.stage);
      const nextStage = stageOrder[stageOrder.indexOf(sectionTab.stage) + 1] || null;
      // A single-section list, so the section's own Frank-mode material-sheet fetch + gating are reused. The
      // KEY is the section (not the stage tab), so the three stage tabs share ONE ManufacturingLineLists
      // instance -- the picked plan and its solved solutions carry across tabs; only stageView changes.
      // Picking a manufacturing line or a solution stays on its own tab (so the selected one, and the dimmed
      // rest, stay visible); the user moves on with the tabs. Visiting the Pricing tab commits + prices the
      // marked solution.
      return (
        <LayoutNavSectionList
          key={`section_${sectionTab.sectionIndex}`}
          navSections={[navSections[sectionTab.sectionIndex]]}
          count={count}
          config={config}
          stageView={sectionTab.stage}
          onGoToNextStage={nextStage ? () => openStage(sectionTab.sectionIndex, nextStage) : null}
          onGoToManufacturingLine={() => openStage(sectionTab.sectionIndex, 'line')}
        />
      );
    }
    return null;
  };

  return (
    <ErrorBoundary componentName="FrankQuoteBuilderPage" errorContext={{ user_quote_id: user_quote_id }}>
      <CacheProvider initialCache={window.user_quote_section_properties_cached}>
        <LayoutContext.Provider value={LayoutContextValues}>
          <DebugPopupProvider>
          <div className={`${styles.component_container} container`}>
            {/* TOP LEVEL: one tab per section plus Manage / Customer / Dieline, on the shared divider, with the
                "Guided setup" button on the right. Aligning to flex-end sits the tab underlines on the border.
                Using the shared .dct-tabs / .dct-tab design-language classes (brand underline on the active
                tab) rather than a per-page inline style. */}
            <div ref={tabRowRef} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', borderBottom: '2px solid var(--rule)' }}>
              <div className="dct-tabs" style={{ flex: 1, border: 'none', justifyContent: 'center', flexWrap: 'wrap' }}>
                {topTabs.map((tab) => {
                  const isActive = tab.sectionIndex != null
                    ? activeSectionIndex === tab.sectionIndex
                    : activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      ref={isActive && tab.sectionIndex != null ? activeTabRef : null}
                      className={`dct-tab${isActive ? ' is-active' : ''}`}
                      style={{ marginBottom: '-2px' }}
                      onClick={() => (tab.sectionIndex != null ? openSection(tab.sectionIndex) : setActiveTab(tab.key))}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
              {/* Reopen the guided setup wizard any time; only shown when there is a part section to set up. */}
              {sectionTabs.length > 0 && (
                <div style={{ paddingBottom: '8px', paddingLeft: '12px' }}>
                  <button
                    type="button"
                    className="dct-button dct-button--secondary dct-button--small"
                    onClick={() => setWizardOpen(true)}
                  >
                    {t('setup_wizard.open')}
                  </button>
                </div>
              )}
            </div>

            {/* SECOND LEVEL: the active section's three stages, in order, shown ONLY when a section tab is
                selected. They are a subordinate segmented control (not a second row of the same underline tab),
                numbered because the user walks them in sequence: pick a Manufacturing Line, then a Solution,
                then see the Pricing. Left-indented by subMenuOffset so the control hangs UNDER its parent
                section tab, like a drop-down sub-menu. The page breadcrumb (parent | model | catalog name) is
                pushed to the right of this same line, so the two share one row and the section content starts
                right below. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', margin: '10px 0 0', minHeight: '34px' }}>
              {isSectionTab && (
                <div className={frankStyles.stageTabs} role="tablist" aria-label={t('quote_builder.frank_section_stages')} style={{ marginLeft: subMenuOffset }}>
                  {sectionStageTabs.map((stageTab, stepIndex) => {
                    const isActive = activeStage === stageTab.stage;
                    return (
                      <button
                        key={stageTab.stage}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={`${frankStyles.stageTab}${isActive ? ` ${frankStyles.stageTabActive}` : ''}`}
                        onClick={() => openStage(activeSectionIndex, stageTab.stage)}
                      >
                        <span className={frankStyles.stageNumber}>{stepIndex + 1}</span>
                        {t(stageTab.labelKey)}
                      </button>
                    );
                  })}
                </div>
              )}
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                {header && (
                  <div className="page-in-name" style={{ fontSize: '16px', margin: 0 }}>
                    <span>
                      {header.demo
                        ? header.demoLabel
                        : <a href={header.parentUrl}>{header.parentName}</a>}
                    </span>
                    {/* The shared `.page-in-name h1` rule forces 23px; override it inline so this breadcrumb
                        sits at the tab-label size. */}
                    <h1 style={{ fontSize: 'inherit' }}>
                      {' | '}<a href={header.groupUrl}>{header.groupName}</a>
                      {' | '}<a href={header.catalogUrl}>{header.catalogName}</a>
                    </h1>
                  </div>
                )}
              </div>
            </div>

            {/* A FORM tab is constrained to a readable column, centered, so its labels sit near their inputs
                rather than stretching label-left / input-right across the whole page. A SECTION tab is not:
                it lists the manufacturing line cards, which are sized as a fraction of this container (4 to a
                row, like the legacy listing) -- in a 620px column each card is ~139px and its machine columns
                are crushed to ~60px. So the section tabs get the container's full width. */}
            {/* The active tab's body is hidden while the wizard is open, so only the wizard's own
                ManufacturingLineLists is mounted (two would fight over the manufacturing_line_<index> URL
                state). Closing the wizard re-renders it, now reading the priced state the wizard persisted. */}
            {!wizardOpen && (
              <div style={isSectionTab || activeTab === 'dieline' ? undefined : { maxWidth: '620px', margin: '0 auto' }}>
                {renderActiveTab()}
              </div>
            )}
          </div>

          {wizardOpen && (
            <FrankSetupWizard
              navSections={navSections}
              config={config}
              onClose={() => setWizardOpen(false)}
            />
          )}
          </DebugPopupProvider>
        </LayoutContext.Provider>
      </CacheProvider>
    </ErrorBoundary>
  );
};

export default FrankQuoteBuilderPage;
